import { create } from 'zustand';

/**
 * Cross-mount selection state for the Compare page (IMP-006).
 *
 * Maria, 2026-06-01 Slack 15:08 (meeting recap):
 *   "Update the last 3 tabs so that they retain the data when a user
 *    clicks away and comes back to it."
 *
 * See `detailSelectionStore.ts` for the architecture rationale
 * (mirroring Map's in-memory zustand pattern rather than URL-sync or
 * persist-middleware).
 *
 * The Compare page maintains three column slots, each independently
 * holding a metro id or null. We expose two write paths:
 *   - `setColumn(index, id)` for the per-column picker
 *   - `setSelectedIds(next)` for the URL-seed bulk-fill path
 * and `clearAll()` for the explicit "Clear all" button.
 *
 * Slot count = 3 mirrors `NUM_COLUMNS` in CompareView. We hard-code
 * it here rather than threading a constant so the store is self-
 * contained -- if the column count ever changes, both call sites
 * need updating anyway and the type signature gives loud feedback.
 */
interface CompareSelectionStore {
  readonly selectedIds: ReadonlyArray<string | null>;
  setColumn: (index: number, id: string | null) => void;
  setSelectedIds: (next: ReadonlyArray<string | null>) => void;
  clearAll: () => void;
}

const NUM_COLUMNS = 3;
const EMPTY: ReadonlyArray<string | null> = [null, null, null];

export const useCompareSelectionStore = create<CompareSelectionStore>((set) => ({
  selectedIds: EMPTY,
  setColumn: (index, id) =>
    set((s) => {
      const next: Array<string | null> = [...s.selectedIds];
      // Defensive: ignore out-of-range writes rather than silently
      // grow the array. CompareView only ever passes 0/1/2 today.
      if (index < 0 || index >= NUM_COLUMNS) return s;
      next[index] = id;
      return { selectedIds: next };
    }),
  setSelectedIds: (next) => set({ selectedIds: [...next] }),
  clearAll: () => set({ selectedIds: EMPTY }),
}));
